// Fires e-commerce events to whichever ad/analytics tags are actually
// configured (Meta Pixel via fbq, GA4 via gtag, GTM via dataLayer) — see
// TrackingScripts.tsx for how those globals get defined in the first place.
// Every call here is a no-op if a given tag isn't loaded (checked via
// typeof, matching TrackingScripts.tsx's own "never assume the ID is set"
// stance), so this is always safe to call unconditionally from anywhere.

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

const CURRENCY = 'BDT';

export interface TrackedItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
}

function pushDataLayer(event: string, payload: Record<string, unknown>) {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...payload });
}

// Cookie consent — a first-party choice (own banner, not a third-party CMP),
// stored once and reused by both TrackingScripts.tsx (to set GA4's consent
// default before it configures, required by Google's Consent Mode setup
// wizard) and CookieConsentBanner.tsx (to apply the visitor's actual
// choice). Until a choice is made, both GA4 and Meta default to denied.
export type ConsentChoice = 'granted' | 'denied';
const CONSENT_KEY = 'rxk_cookie_consent';

export function getStoredConsent(): ConsentChoice | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const v = localStorage.getItem(CONSENT_KEY);
    return v === 'granted' || v === 'denied' ? v : null;
  } catch {
    return null;
  }
}

export function setStoredConsent(value: ConsentChoice): void {
  try {
    localStorage.setItem(CONSENT_KEY, value);
  } catch {
    // localStorage unavailable (private mode, quota) — the choice just
    // won't persist across visits; this tab's own gtag/fbq calls still run.
  }
}

export function applyConsent(value: ConsentChoice): void {
  if (typeof window === 'undefined') return;
  window.gtag?.('consent', 'update', {
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
    analytics_storage: value,
  });
  window.fbq?.('consent', value === 'granted' ? 'grant' : 'revoke');
}

function gaItems(items: TrackedItem[]) {
  return items.map((i) => ({ item_id: i.id, item_name: i.name, price: i.price, quantity: i.quantity }));
}

// The Pixel/GA4 bootstrap snippets in TrackingScripts.tsx each fire exactly
// one PageView/page_view at script-load time — correct for a classic
// multi-page site, but this is an SPA: client-side route changes never
// reload the page, so without this, every session only ever reports one
// PageView for whatever URL happened to be current when the scripts first
// loaded. Called from TrackingScripts.tsx on every route change after the
// first (the bootstrap scripts already cover that one).
export function trackPageView(path: string) {
  if (typeof window === 'undefined') return;
  window.fbq?.('track', 'PageView');
  window.gtag?.('event', 'page_view', { page_path: path });
  pushDataLayer('page_view', { page_path: path });
}

export function trackViewContent(item: { id: string; name: string; price: number }) {
  if (typeof window === 'undefined') return;
  window.fbq?.('track', 'ViewContent', {
    content_ids: [item.id],
    content_name: item.name,
    content_type: 'product',
    value: item.price,
    currency: CURRENCY,
  });
  window.gtag?.('event', 'view_item', {
    currency: CURRENCY,
    value: item.price,
    items: gaItems([{ ...item, quantity: 1 }]),
  });
  pushDataLayer('view_item', { ecommerce: { currency: CURRENCY, value: item.price, items: gaItems([{ ...item, quantity: 1 }]) } });
}

export function trackAddToCart(item: { id: string; name: string; price: number; quantity?: number }) {
  if (typeof window === 'undefined') return;
  const quantity = item.quantity ?? 1;
  const value = item.price * quantity;
  window.fbq?.('track', 'AddToCart', {
    content_ids: [item.id],
    content_name: item.name,
    content_type: 'product',
    value,
    currency: CURRENCY,
  });
  window.gtag?.('event', 'add_to_cart', {
    currency: CURRENCY,
    value,
    items: gaItems([{ ...item, quantity }]),
  });
  pushDataLayer('add_to_cart', { ecommerce: { currency: CURRENCY, value, items: gaItems([{ ...item, quantity }]) } });
}

export function trackInitiateCheckout(items: TrackedItem[], value: number) {
  if (typeof window === 'undefined') return;
  window.fbq?.('track', 'InitiateCheckout', {
    content_ids: items.map((i) => i.id),
    contents: items.map((i) => ({ id: i.id, quantity: i.quantity })),
    num_items: items.reduce((n, i) => n + i.quantity, 0),
    value,
    currency: CURRENCY,
  });
  window.gtag?.('event', 'begin_checkout', {
    currency: CURRENCY,
    value,
    items: gaItems(items),
  });
  pushDataLayer('begin_checkout', { ecommerce: { currency: CURRENCY, value, items: gaItems(items) } });
}

export function trackPurchase(orderId: string, items: TrackedItem[], value: number) {
  if (typeof window === 'undefined') return;
  // eventID must match the event_id the server's Meta Conversions API call
  // uses for this same order (core-place-order / core-sslcommerz-callback,
  // both keyed on order_no/order_id) — otherwise Meta counts this Purchase
  // twice, once from the browser and once from the server.
  window.fbq?.('track', 'Purchase', {
    content_ids: items.map((i) => i.id),
    contents: items.map((i) => ({ id: i.id, quantity: i.quantity })),
    content_type: 'product',
    value,
    currency: CURRENCY,
  }, { eventID: orderId });
  window.gtag?.('event', 'purchase', {
    transaction_id: orderId,
    currency: CURRENCY,
    value,
    items: gaItems(items),
  });
  pushDataLayer('purchase', { ecommerce: { transaction_id: orderId, currency: CURRENCY, value, items: gaItems(items) } });
}

// Meta Pixel's own first-party cookies — _fbp (browser id, set once the
// pixel loads) and _fbc (click id, only set when the visitor arrived via an
// ad link with ?fbclid=...). coreOrder.ts forwards these with the order so
// the server-side Purchase event (core-place-order / core-sslcommerz-
// callback) can include them — raises Meta's match quality well beyond
// hashed phone/email alone, since it lets Meta attribute the sale back to
// the exact ad click instead of just a phone number.
export function getFbCookies(): { fbp: string | null; fbc: string | null } {
  if (typeof document === 'undefined') return { fbp: null, fbc: null };
  const read = (name: string): string | null => {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
  };
  return { fbp: read('_fbp'), fbc: read('_fbc') };
}
